import {defaultError, WebError} from "@hybird/core/error";

function isAsyncFunction(fn) {
  let fnStr = fn.toString()
  return Object.prototype.toString.call(fn) === '[object AsyncFunction]' || fnStr.includes("return _regenerator.default.async(function")
}

Function.prototype.compose = function (before) {
  const self = this;
  return function () {
    const result = before.apply(self, arguments);
    if (result instanceof Promise) {
      return result.then(data => {
        return self.apply(self, data ? [data] : arguments);
      })
    } else {
      return self.apply(self, result ? [result] : arguments);
    }
  }
}

Function.prototype.andThen = function (after) {
  const self = this;
  return function () {
    const result = self.apply(self, arguments);
    if (result instanceof Promise) {
      return result.then(data => {
        return after.apply(self, data ? [data] : arguments);
      })
    } else {
      return after.apply(self, result ? [result] : arguments);
    }
  }
}

class ProxyMethod {
  /**
   * @type{Object}
   */
  handle;

  methodName;

  /**
   * @type{Interceptor[]}
   */
  _interceptors = [];

  /**
   * @type{MethodInterceptor[]}
   */
  _methodInterceptors = [];

  /**
   * @type{MethodInvoke}
   * @private
   */
  _methodInvoke;

  get interceptors() {
    const interceptors = [];
    interceptors.push.apply(interceptors, this._interceptors);
    interceptors.push.apply(interceptors, this._methodInterceptors);

    return interceptors;
  }

  /**
   *
   * @param handle{Object}
   * @param methodName{String}
   * @param interceptors{MethodInterceptor | Interceptor |[MethodInterceptor | Interceptor]?}
   */
  constructor(handle, methodName, interceptors) {
    this.handle = handle;
    this.methodName = methodName;
    this.addInterceptor(interceptors);

    this.target = handle[methodName];

    if (typeof this.target !== "function") {
      throw WebError.code(defaultError.SYSTEM_ERROR, {
        msg: "不允许代理非方法"
      });
    }

    if (!isAsyncFunction(this.target)) {
      console.warn(handle, methodName, "已将方法代理为异步方法");
    }

    const self = this;

    this.handle[methodName] = async function (a1, a2, a3, a4, a5) {
      let params = []

      for (let i in arguments) {
        params[i] = arguments[i];
      }

      try {
        for (let i in self._interceptors) {
          const interceptor = self._interceptors[i];
          await interceptor["preHandle"]?.call(interceptor, params);
        }

        let result;

        result = await self.target.apply(self.handle, params);

        for (let i in self._interceptors) {
          const interceptor = self._interceptors[i];
          await interceptor["postHandle"]?.call(interceptor, arguments, result);
        }

        for (let i in self._interceptors) {
          const interceptor = self._interceptors[i];
          await interceptor["afterCompletion"]?.call(interceptor, arguments, result);
        }

        return result;
      } catch (e) {
        for (let i in self._interceptors) {
          const interceptor = self._interceptors[i];
          await interceptor["afterCompletion"]?.call(interceptor, arguments, null, e);
        }
        throw e;
      }
    }
  }

  addInterceptor(interceptor) {
    if (!interceptor) {
      return;
    }

    const add = (interceptor) => {
      if (interceptor instanceof MethodInterceptor) {
        this.addMethodInterceptor(interceptor);
      } else if (interceptor instanceof Interceptor && !this._interceptors.includes(interceptor)) {
        this._interceptors.push(interceptor);
      }
    }

    if (interceptor instanceof Array) {
      interceptor.forEach(i => {
        add(i);
      })
    } else {
      add(interceptor);
    }

    this._interceptors.sort((o, n) => o.order ? o.order : 50 - n.order ? n.order : 50)
  }

  addMethodInterceptor(methodInterceptor) {
    if (!this._methodInterceptors.includes(methodInterceptor)) {
      if (!this._methodInvoke) {
        this._methodInvoke = new MethodInvoke(this, this.target);
        this.target = this._methodInvoke.invoke.bind(this._methodInvoke);
      }

      this._methodInvoke.addMethodInterceptor(methodInterceptor);
      this._methodInterceptors.push(methodInterceptor)
    }
  }
}

class MethodInvoke {
  target;

  proxy;

  _methodInterceptors = [];

  constructor(proxy, target) {
    this.proxy = proxy;
    this.target = target;
  }

  async invoke() {
    let params = []

    for (let i in arguments) {
      params[i] = arguments[i];
    }

    const next = async (index) => {
      const methodInterceptor = this._methodInterceptors[index];

      if (methodInterceptor) {
        return methodInterceptor.invoke({
          self: this.proxy.handle,
          params: params,
          methodName: this.proxy.methodName,
          target: this.target,
          proceed: async function () {
            params = arguments.length > 0 ? arguments : params;
            return next(++index);
          }.bind(this)
        })
      } else {
        return this.target.apply(this.proxy.handle, params);
      }
    }

    return await next(0);
  }

  addMethodInterceptor(interceptor) {
    this._methodInterceptors.push(interceptor);
    this._methodInterceptors.sort((o, n) => o.order ? o.order : 50 - n.order ? n.order : 50)
  }
}

class Interceptor {

  async preHandle(params) {

  }

  async postHandle(params, result) {

  }

  async afterCompletion(params, result, exception) {
  }

  get order() {
    return 50;
  }
}

class MethodInterceptor {
  invoke({
           self,
           params,
           proceed
         }) {
    return proceed();
  }
}

export {
  ProxyMethod, Interceptor, isAsyncFunction, MethodInterceptor
}